<script setup>
import emitter from "@/utils/bus"; // 通信对象
import { ref, computed, onMounted, onUnmounted, inject } from "vue";
import { useConfigStore } from "@/store/config";
import { useUserStore } from "@/store/user";
import { useI18n } from "vue-i18n";
import {
  appHapticFeedback,
  setLocalStorage,
  openUrl,
  getCacheV1,
  isSupportedVersion,
  getLocalStorage,
  trimTrailingZeros,
  _twa_decode,
  addCommasToNumber,
} from "@/utils/common";
import IndexShare from "@/components/Share/index.vue";
import { fetchShareinfo } from "@/api/task";
import { useRouter } from "vue-router";
const { t } = useI18n();

const config = useConfigStore();
const userStore = useUserStore();
const isShow = ref(false);
const limitText = ref("");
const shareGroupsNum = computed(() => userStore.share_group_nums);
const visible = ref(false);
const post_link = computed(() => config.post_link);
const router = useRouter();
// 任务完成数量（响应式）
const taskCompleteNum = ref(Number(getLocalStorage("task_complete_num") || 0));
// 发送消息次数（响应式）
const postMessageNum = ref(Number(getLocalStorage("post_message_num") || 0));
const showType = ref({
  type: "",
  num: 0, //总数量
  message: "",
  need_num: 0, //需要数量
  price: 0, //价格
  equipment_id: 0, //商品id
  // 进度相关（从 WalletPopup 透传）
  completed_steps: [],
  retry_type: "",
  sourceId: "",
});

// 进度条初始值：从配置读取 progress_start（0-100）
const initProgress = computed(() => {
  let val = 0;
  // 优先通过 getConfig 读取（兼容 extraConfig）
  if (typeof config.getConfig === "function") {
    const cfgVal = config.getConfig("progress_start");
    if (cfgVal !== undefined && cfgVal !== null && cfgVal !== "") {
      val = Number(cfgVal);
    }
  }
  // 兜底直接读取属性（若后端直接下发到 state）
  if ((val === 0 || Number.isNaN(val)) && config.progress_start !== undefined) {
    val = Number(config.progress_start);
  }
  if (Number.isNaN(val)) val = 0;
  return Math.min(100, Math.max(0, val));
});
// withdraw_step 总数量
const totalSteps = computed(() => {
  const steps = config.withdraw_step;
  return Array.isArray(steps) ? steps.length : 0;
});
// 已完成数量（来自 convert.data.completed_steps.length）
const completedCount = computed(() => showType.value.completed_steps?.length || 0);
// 进度条当前百分比计算：初始值 + 递减权重分配（从大到小直到满进度）
const progressPercent = computed(() => {
  if (!totalSteps.value) return initProgress.value;
  const remaining = Math.max(0, 100 - initProgress.value);
  const n = totalSteps.value;
  const k = Math.min(completedCount.value, n);
  // 线性递减权重：w_i = n - i + 1（i从1到n）
  // sum_w = n*(n+1)/2
  // 已完成k步的权重和：sum_first_k = k*(2n - k + 1)/2
  const sumW = (n * (n + 1)) / 2;
  const sumFirstK = (k * (2 * n - k + 1)) / 2;
  const ratio = sumW ? sumFirstK / sumW : 0;
  const extra = remaining * ratio;
  return Math.min(100, Math.max(0, initProgress.value + extra));
});

// 进度条下方的提示文案，来自配置 progress_text
const progressText = computed(() => {
  if (typeof config.getConfig === "function") {
    const v = config.getConfig("progress_text");
    if (v !== undefined && v !== null) return v;
  }
  return config.progress_text || "";
});

// 展示用百分比：未全部完成时避免四舍五入到100%，保留一位小数向下取
const displayPercent = computed(() => {
  const n = totalSteps.value;
  const k = completedCount.value;
  const raw = Number(progressPercent.value) || 0;
  if (n && k >= n) return 100;
  // 向下取一位小数，最多 99.9
  const floored = Math.floor(raw * 10) / 10;
  return Math.min(99.9, floored);
});

const shareUrl = computed(() => config.app_link);
const shareTitle = computed(() => config.share_title);
const shareTwaid = computed(() => userStore.twa_id);
const shareThemesColor = computed(() => config.themes_color);
// 支付相关状态
const isPageLoading = ref(false); // 页面跳转加载状态
const $swal = inject("$swal");
emitter.on("showWithdrawTip", (res) => {
  console.log(res);
  isShow.value = res.show;
  showType.value = {
    ...showType.value,
    content_type: res.content_type || 2,
    type: res.type,
    message: res.message || res.type,
    num: res.num || 0,
    need_num: res.need_num || 0,
    price: res.price || 0,
    equipment_id: res.equipment_id || 0,
    completed_steps: res.completed_steps || [],
    retry_type: res.retry_type || "",
    sourceId: res.sourceId || "",
  };

  let text = res.message || "";
  limitText.value = text.replace("{num}", addCommasToNumber(res.need_num));
  emitter.emit("withdrawShow", false);
});

// 监听任务完成事件，更新任务完成数量
emitter.on("taskCompleted", () => {
  const newTaskCompleteNum = Number(getLocalStorage("task_complete_num") || 0);
  taskCompleteNum.value = newTaskCompleteNum;

  if (
    showType.value.type === "task_complete" &&
    newTaskCompleteNum >= showType.value.num
  ) {
    close();
    emitRetryWithdraw();
  }
});

const emit = defineEmits(["start"]);

const emitRetryWithdraw = () => {
  if (showType.value.retry_type !== "withdraw" || !showType.value.sourceId) {
    return;
  }

  emitter.emit("retryWithdraw", {
    retryType: showType.value.retry_type,
    sourceId: showType.value.sourceId,
  });

  showType.value = {
    ...showType.value,
    retry_type: "",
    sourceId: "",
  };
};

// 分享到群
let shareOnClick = async () => {
  appHapticFeedback();
  let share_id = getCacheV1("share_id");
  if (share_id && isSupportedVersion("8.0")) {
    // 支持卡片模式分享
    window.Telegram.WebApp.shareMessage(share_id, (success) => {
      if (success) {
        userStore.addShareGroupNum();
        setLocalStorage("share_group_nums", userStore.share_group_nums);
        if (userStore.share_group_nums >= showType.value.num) {
          close();
          emitRetryWithdraw();
        }
      }
    });
  } else {
    // 不支持卡片模式分享
    emitter.emit("share", true);
    userStore.addShareGroupNum();
    setLocalStorage("share_group_nums", userStore.share_group_nums);
    if (userStore.share_group_nums >= showType.value.num) {
      close();
      emitRetryWithdraw();
    }
  }
};

// 关闭
let close = () => {
  isShow.value = false;
  appHapticFeedback();
};

// 绑定手机
let handleBindPhone = () => {
  appHapticFeedback();
  openUrl(config.Phone_url);
  close();
  window.Telegram.WebApp.close();
};

// 绑定钱包
let handleBindWallet = () => {
  close();
  emitter.emit("showEditWallet", true);
};

// 兑换
let exchange = () => {
  close();
  emitter.emit("exchangeShow", true);
};

let singlePay = async () => {
  emitter.emit("getPaymentList", {
    type: showType.value.content_type,
    sku_id: showType.value.equipment_id,
  });
};

// 关闭支付弹窗
const handleClosePayment = () => {
  console.log("Closing payment modal");
  visible.value = false;
  // 关闭WithdrawTip弹窗
  close();
};
async function sendMessageFunc() {
  let user_name = JSON.parse(getLocalStorage("user_name_list"));
  let send_num = Number(getLocalStorage("post_message_num"));
  if (!user_name) {
    const res = await fetchShareinfo();
    if (res.code == 200) {
      user_name = JSON.parse(_twa_decode(res.data));
      // 序列化存储
      setLocalStorage("user_name_list", JSON.stringify(user_name));
    }
  }
  let success = await sendMessage(user_name);
  if (success) {
    const new_send_num = send_num + 1;
    setLocalStorage("post_message_num", new_send_num);
    postMessageNum.value = new_send_num; // 更新UI
    if (new_send_num >= showType.value.num) {
      close();
      emitRetryWithdraw();
    }
  }
}
function sendMessage(user_name_list) {
  return new Promise((resolve) => {
    let user_name =
      user_name_list[Math.floor(Math.random() * user_name_list.length)];
    let user_name_list_v1 = JSON.stringify(
      user_name_list.filter((name) => name !== user_name)
    );

    setLocalStorage(`user_name_list`, user_name_list_v1);

    // 打开Telegram链接
    Telegram.WebApp.openTelegramLink(
      "https://t.me/" + user_name + "?text=" + encodeURIComponent(post_link.value)
    );

    // 4秒后认为发送成功
    setTimeout(() => {
      resolve(true); // 返回成功
    }, 4000);
  });
}
/**
 * 任务完成后跳转到首页并滚动到任务部分
 */
function taskCompleteFunc() {
  // 关闭当前弹窗
  close();
  emitter.emit('showTask')
}

// 组件卸载时清理事件监听器
onUnmounted(() => {
  emitter.off("showWithdrawTip");
  emitter.off("taskCompleted");
});
</script>

<template>
  <div class="view" v-show="isShow">
    <div class="language">
      <div v-if="showType.type === 'invite_friends'">
        <div class="top">
          <div class="top_title">{{ $t("message.Withdrawal") }}</div>
          <div class="close" @click="close">
            <img src="@/assets/icon/close.svg" alt="" />
          </div>
        </div>
        <div class="content">
          {{ limitText }}
        </div>
        <div class="progress-text">
          {{
            $t("message.Numberoffriends", {
              now: addCommasToNumber(userStore.referrals),
              limit: addCommasToNumber(showType.num || 0),
            })
          }}
        </div>
        <div class="button" @click="shareOnClick">
          {{ $t("message.ShareToGroups") }}
        </div>
        <div class="share-button">
          <IndexShare :url="shareUrl" :title="shareTitle" :twa_id="shareTwaid" :themes_color="shareThemesColor" />
        </div>
        <!-- 支付跳过该步骤 -->
        <button v-if="showType.price && showType.type !== 'single_pay'" type="button" class="price-skip"
          @click="singlePay">
            {{
              config.pay_skip_tip.replace(
                "{num}",
                addCommasToNumber(showType.price) + " " + config.denominated_coin
              )
            }}
        </button>
      </div>
      <div v-if="showType.type === 'share_groups'">
        <div class="top">
          <div class="top_title">{{ $t("message.Withdrawal") }}</div>
          <div class="close" @click="close">
            <img src="@/assets/icon/close.svg" alt="" />
          </div>
        </div>
        <div class="content">
          {{ limitText }}
        </div>
        <div class="progress-text">
          {{
            $t("message.NumberofGroups", {
              now: addCommasToNumber(shareGroupsNum || 0),
              limit: addCommasToNumber(showType.num || 0),
            })
          }}
        </div>
        <div class="button" @click="shareOnClick">
          {{ $t("message.ShareToGroups") }}
        </div>
      </div>
      <div v-if="showType.type === 'phone'">
        <div class="top">
          <div class="top_title">{{ $t("message.Withdrawal") }}</div>
          <div class="close" @click="close">
            <img src="@/assets/icon/close.svg" alt="" />
          </div>
        </div>
        <div class="content">
          {{ limitText }}
        </div>
        <div class="button" @click="handleBindPhone">
          {{ $t("message.Confirm") }}
        </div>
      </div>
      <div v-if="showType.type === 'wallet'">
        <div class="top">
          <div class="top_title">{{ $t("message.Withdrawal") }}</div>
          <div class="close" @click="close">
            <img src="@/assets/icon/close.svg" alt="" />
          </div>
        </div>
        <div class="content">
          {{ limitText }}
        </div>
        <div class="button" @click="handleBindWallet">
          {{ $t("message.Confirm") }}
        </div>
      </div>
      <div v-if="showType.type === 'sub_wallet'">
        <div class="top">
          <div class="top_title">{{ $t("message.Withdrawal") }}</div>
          <div class="close" @click="close">
            <img src="@/assets/icon/close.svg" alt="" />
          </div>
        </div>
        <div class="content">
          {{ limitText }}
        </div>
        <div class="button" @click="close">
          {{ $t("message.Confirm") }}
        </div>
      </div>
      <div v-if="showType.type === 'sub_phone'">
        <div class="top">
          <div class="top_title">{{ $t("message.Withdrawal") }}</div>
          <div class="close" @click="close">
            <img src="@/assets/icon/close.svg" alt="" />
          </div>
        </div>
        <div class="content">
          {{ limitText }}
        </div>
        <div class="button" @click="close">
          {{ $t("message.Confirm") }}
        </div>
      </div>
      <div v-if="showType.type === 'sub_pay'">
        <div class="top">
          <div class="top_title">{{ $t("message.Withdrawal") }}</div>
          <div class="close" @click="close">
            <img src="@/assets/icon/close.svg" alt="" />
          </div>
        </div>
        <div class="content">
          {{ limitText }}
        </div>
        <div class="button" @click="close">
          {{ $t("message.Confirm") }}
        </div>
      </div>
      <div v-if="showType.type === 'reward_withdrawal'">
        <div class="top">
          <div class="top_title">{{ $t("message.Withdrawal") }}</div>
          <div class="close" @click="close">
            <img src="@/assets/icon/close.svg" alt="" />
          </div>
        </div>
        <div class="content">
          {{ limitText }}
        </div>
        <div class="button" @click="close">
          {{ $t("message.Confirm") }}
        </div>
      </div>
      <div v-if="showType.type === 'pay'">
        <div class="top">
          <div class="top_title">{{ $t("message.Withdrawal") }}</div>
          <div class="close" @click="close">
            <img src="@/assets/icon/close.svg" alt="" />
          </div>
        </div>
        <div class="content">
          {{ limitText }}
        </div>
        <div class="button" @click="close">
          {{ $t("message.Confirm") }}
        </div>
      </div>
      <div v-if="showType.type === 'send_message'">
        <div class="top">
          <div class="top_title">{{ $t("message.Withdrawal") }}</div>
          <div class="close" @click="close">
            <img src="@/assets/icon/close.svg" alt="" />
          </div>
        </div>
        <div class="content">
          {{ limitText }}
        </div>
        <div class="progress-text">
          {{
            $t("message.NumberofShares", {
              now: addCommasToNumber(postMessageNum || 0),
              limit: addCommasToNumber(showType.num || 0),
            })
          }}
        </div>
        <div class="button" @click="sendMessageFunc">
          {{ $t("message.SendMessage") }}
        </div>
      </div>
      <div v-if="showType.type === 'exchange'">
        <div class="top">
          <div class="top_title">
            {{
              showType.type === "exchange"
                ? $t("message.Exchange")
                : $t("message.Withdrawal")
            }}
          </div>
          <div class="close" @click="close">
            <img src="@/assets/icon/close.svg" alt="" />
          </div>
        </div>
        <div class="content">
          {{ limitText }}
        </div>
        <div class="button" @click="exchange">
          {{ $t("message.Exchange") }}
        </div>
      </div>
      <div v-if="showType.type === 'single_pay'">
        <div class="top">
          <div class="top_title">
            {{
              showType.type === "exchange"
                ? $t("message.Exchange")
                : $t("message.Withdrawal")
            }}
          </div>
          <div class="close" @click="close">
            <img src="@/assets/icon/close.svg" alt="" />
          </div>
        </div>
        <div class="content">
          {{ limitText }}
        </div>
        <div class="button" @click="singlePay">
          {{ $t("message.PayNow") }}
        </div>
      </div>
      <div v-if="showType.type === 'task_complete'">
        <div class="top">
          <div class="top_title">{{ $t("message.Withdrawal") }}</div>
          <div class="close" @click="close">
            <img src="@/assets/icon/close.svg" alt="" />
          </div>
        </div>
        <div class="content">
          {{ limitText }}
        </div>
        <div class="progress-text">
          {{
            $t("message.NumberofTasks", {
              now: addCommasToNumber(taskCompleteNum || 0),
              limit: addCommasToNumber(showType.num || 0),
            })
          }}
        </div>
        <div class="button" @click="taskCompleteFunc">
          {{ $t("message.CompleteTask") }}
        </div>
      </div>
      <!-- 支付跳过该步骤 -->
      <button v-if="showType.price && showType.type !== 'single_pay' && showType.type !== 'invite_friends'"
        type="button" class="price-skip" @click="singlePay">
          {{
            config.pay_skip_tip.replace(
              "{num}",
              addCommasToNumber(showType.price) + " " + config.denominated_coin
            )
          }}
      </button>
      <!-- 进度条展示 -->
      <div v-if="showType.type !== 'exchange'" class="withdraw-steps"
        :style="{ marginTop: !showType.price ? '3vw' : '6vw' }">
        <div class="withdraw-steps-bar">
          <div class="withdraw-steps-fill"
            :style="{ width: displayPercent + '%', backgroundColor: config.themes_color }"></div>
          <div class="withdraw-steps-overlay">{{ trimTrailingZeros(displayPercent.toFixed(1)) }}%</div>
        </div>
        <div class="withdraw-steps-subtext">{{ progressText }}</div>
      </div>
    </div>

    <!-- 页面跳转加载效果 -->
    <div v-if="isPageLoading" class="modal-loading">
      <div class="modal-content">
        <svg t="1729145003684" class="loading-img" viewBox="0 0 1024 1024" version="1.1"
          xmlns="http://www.w3.org/2000/svg" p-id="9759" width="128" height="128">
          <path
            d="M663.2 251.3l-75.7 130.5c-22.2-12.8-48-20.3-75.5-20.3-3.1 0-6 0.3-9 0.5l-11.3-186-130.9 75.3 75.7 130.5c-25.6 14.7-46.3 36.6-59.6 63l-167.3-83.3V512h151.2c0 30.7 9.3 59.1 25.1 82.9l-156 102.5 130.9 75.3 75.7-130.5c22.2 12.8 48 20.3 75.5 20.3 3.1 0 6-0.3 9-0.5l11.3 186 130.9-75.3-75.7-130.5c25.6-14.7 46.3-36.6 59.6-63l167.3 83.3V512H663.2c0-30.7-9.3-59.1-25.1-82.9l156-102.6-130.9-75.2zM512 587.3c-41.8 0-75.6-33.7-75.6-75.3s33.8-75.3 75.6-75.3 75.6 33.7 75.6 75.3-33.8 75.3-75.6 75.3z"
            fill="currentColor" p-id="9760"></path>
          <path
            d="M64 64v896h896V64H64z m100.8 67.2c18.6 0 33.6 15 33.6 33.6s-15 33.6-33.6 33.6-33.6-15-33.6-33.6 15-33.6 33.6-33.6z m0 761.6c-18.6 0-33.6-15-33.6-33.6s15-33.6 33.6-33.6 33.6 15 33.6 33.6-15 33.6-33.6 33.6z m694.4 0c-18.6 0-33.6-15-33.6-33.6s15-33.6 33.6-33.6 33.6 15 33.6 33.6-15 33.6-33.6 33.6z m-347.2 0c-210.3 0-380.8-170.5-380.8-380.8S301.7 131.2 512 131.2 892.8 301.7 892.8 512 722.3 892.8 512 892.8z m347.2-694.4c-18.6 0-33.6-15-33.6-33.6s15-33.6 33.6-33.6 33.6 15 33.6 33.6-15 33.6-33.6 33.6z"
            fill="currentColor" p-id="9761"></path>
        </svg>
        <div class="loading-text">{{ t("message.loading") }}</div>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
@use "@/styles/surfaces" as surface;

.view {
  @include surface.dialog-overlay;
  width: 100%;
  background-color: rgba(0, 0, 0, 0.8);
  position: fixed;
  top: 0;
  left: 0;
  z-index: 999;
  display: flex;
  align-items: center;
  backdrop-filter: blur(5px);

  .language {
    @include surface.dialog-panel;
    background: #1a1a1a;
    display: block;
    box-shadow: 0 0 20px 0 rgba(0, 0, 0, 0.5);
    border: 1px solid #333;

    .price-skip {
      @include surface.button;
      margin: 8px 0 0;
      width: 100%;
      border: 0;
      background-color: v-bind("config.themes_color");
      color: #fff;
      text-align: center;
      font-weight: 600;
      cursor: pointer;
    }

    .withdraw-steps {
      margin-top: 8px;

      .withdraw-steps-bar {
        height: 16px;
        border-radius: 6px;
        width: 100%;
        background-color: #333;
        overflow: hidden;
        border: 1px solid #555;
        position: relative;
      }

      .withdraw-steps-fill {
        border-radius: 6px;
        height: 100%;
        transition: width 0.3s ease;
      }

      .withdraw-steps-overlay {
        font-size: 12px;
        position: absolute;
        inset: 0;
        display: flex;
        align-items: center;
        justify-content: center;
        color: #fff;
        pointer-events: none;
        font-weight: 600;
      }

      .withdraw-steps-subtext {
        font-size: 13px;
        line-height: 1.5;
        margin-top: 6px;
        text-align: center;
        color: #e0e0e0;
        font-weight: 600;
      }
    }

    .content {
      margin-top: 8px;
      margin-bottom: 8px;
      font-size: 14px;
      line-height: 1.6;
      color: #e0e0e0;
      white-space: pre-wrap;
    }

    .progress-text {
      font-size: 13px;
      line-height: 1.5;
      margin-bottom: 6px;
      display: flex;
      justify-content: center;
      color: #ff6b6b;
    }

    .friends-num {
      text-align: center;
      font-size: 3.5vw;
      padding: 0vw 1vw;

      .progress-bar {
        width: 100%;
        height: 3vw;
        background-color: #333;
        border-radius: 1.5vw;
        overflow: hidden;
        border: 1px solid #555;
      }

      .progress-fill {
        height: 100%;
        border-radius: 1.5vw;
        transition: width 0.3s ease;
      }
    }

    .partner {
      margin-bottom: 5vw;
      text-align: center;

      .partner-title {
        text-align: center;
        font-size: 3.5vw;
        font-weight: bold;
        color: #e0e0e0;
      }

      .partner-image {
        margin-top: 2vw;
        width: 76vw;
      }
    }

    .share-button {
      margin-top: 8px;
    }

    .button {
      @include surface.button;
      margin-top: 8px;
      width: 100%;
      text-align: center;
      font-weight: bold;
      color: #fff;
      background-color: v-bind("config.themes_color");
    }

    .top {
      @include surface.dialog-header;
      position: relative;

      .close {
        @include surface.close-button;
        background-color: none;

        img {
          width: 100%;
          height: 100%;
          filter: brightness(0) invert(1);
        }
      }

      &_title {
        @include surface.title;
        width: auto;
        font-weight: bold;
        color: #fff;
      }

      &_introduced {
        color: #e0e0e0;
        font-size: 4vw;
      }
    }

    .input-wrapper {
      margin: 8px 0;

      .phone-input {
        width: 100%;
        height: 40px;
        padding: 0 12px;
        font-size: 16px;
        border-radius: 10px;
        box-sizing: border-box;
        background: #2a2a2a;
        border: 1px solid #555;
        color: #fff;

        &::placeholder {
          color: #999;
        }

        &:focus {
          outline: none;
          border-color: v-bind("config.themes_color");
        }
      }

      .error-message {
        color: #ff6b6b;
        font-size: 3vw;
        margin-top: 2vw;
        padding-left: 4px;
      }
    }
  }
}

/* 页面跳转加载效果样式 */
.modal-loading {
  position: fixed;
  z-index: 9999;
  inset: 0;
  background: rgba(40, 40, 40, 0.55);
  display: flex;
  align-items: center;
  justify-content: center;
  backdrop-filter: blur(2px);
}

.modal-content {
  padding: 16px;
  display: flex;
  flex-direction: column;
  align-items: center;
}

.loading-img {
  width: 48px;
  height: 48px;
  margin-bottom: 1vw;
  animation: spin 1s linear infinite;
}

.loading-text {
  color: #fff;
  font-size: 18px;
  margin-top: 6px;
}

@keyframes spin {
  0% {
    transform: rotate(0deg);
  }

  100% {
    transform: rotate(360deg);
  }
}
.view .language .top .close img {
  width: 24px;
  height: 24px;
}
</style>
